import * as THREE from 'three';
import { afterSkeletonUpdate, figureTexels, reserveTexels, touchTexels, withFigureTexels } from './figure-texels.js';

/**
 * Skirts, gowns and loincloths hang from the hips and are skinned to them, so a raised leg would pass through the cloth.
 * Each cloth vertex therefore carries a `clothGap` (the layer's thickness above the body). The legs are two chains of tapered
 * capsules following the thighs and shins. Each capsule carries a vertex along one line: the vertex's horizontal outward
 * direction square to the leg, its rise or fall across the leg growing up to twofold as the leg tilts to level (where a raised
 * thigh runs along the outward direction the cloth is lifted over it or dropped under it, and neighbouring normals that differ
 * sideways still agree). A vertex whose line passes through the capsule, inside it or on its body side, goes to the far side, so the leg shows through nowhere and
 * the cloth stretches over it. Beside that shadow, out to twice the radius, the cloth is drawn to a surface that leaves the leg's
 * silhouette and falls back towards the body ever more steeply, so the carry shrinks smoothly to nothing there and neighbouring
 * vertices stay joined. A layer is held `clothGap` outside the leg, so stacked layers keep their order. The vertex shader does
 * the work after skinning; `pushFromLegs` is its exact CPU twin for tests and resting gaps.
 */
export const CLOTH_GAP = 'clothGap';
/**
 * The stored `clothGap` of a vertex that is not cloth. It is 0 because a mesh without the attribute reads 0 in the shader: weapons
 * and props share the cloth's materials, and must be left alone. A cloth vertex stores its gap plus `CLOTH_BIAS`, which keeps it above 0.
 */
export const CLOTH_OFF = 0;
export const CLOTH_BIAS = 1;
/** How far the capsules are padded beyond the leg's surface: where the cloth rises over a leg the carry is steepest at the leg's edge, and a flat triangle must not reach the leg there. */
export const LEG_PAD = .03;

/** Where a point sits along the leg: `bone` 0 is the thigh (hip to knee), 1 the shin (knee to ankle), `along` runs 0 to 1. */
export interface LegSample { bone: 0 | 1; along: number; radius: number }

const SIDES = ['L', 'R'] as const;

export function legCollision(root: THREE.Object3D, joints: Map<string, THREE.Bone>, samples: LegSample[]) {
  const count = samples.length, chain = Array.from({ length: count * 2 }, () => new THREE.Vector4());
  let skeleton: THREE.Skeleton | undefined, base = -1;
  const inverse = new THREE.Matrix4(), hip = new THREE.Vector3(), knee = new THREE.Vector3(), ankle = new THREE.Vector3(), at = new THREE.Vector3();
  const bones = SIDES.map(side => ['thigh', 'shin', 'foot'].map(name => joints.get(`${name}.${side}`)!));
  /** Place both legs' capsule chains in the rig's own space; call with current world matrices, just before drawing. */
  const update = () => {
    inverse.copy(root.matrixWorld).invert();
    bones.forEach(([thigh, shin, foot], side) => {
      hip.setFromMatrixPosition(thigh.matrixWorld).applyMatrix4(inverse);
      knee.setFromMatrixPosition(shin.matrixWorld).applyMatrix4(inverse);
      ankle.setFromMatrixPosition(foot.matrixWorld).applyMatrix4(inverse);
      samples.forEach(({ bone, along, radius }, i) => {
        at.copy(bone ? knee : hip).lerp(bone ? ankle : knee, along);
        chain[side * count + i].set(at.x, at.y, at.z, radius);
      });
    });
    if (!skeleton) return;
    // Only a moved chain marks the texture changed; three re-sends it anyway in any frame the skeleton moves.
    const data = figureTexels(skeleton); let moved = false;
    chain.forEach((v, i) => { const o = (base + i) * 4; if (data[o] !== Math.fround(v.x) || data[o + 1] !== Math.fround(v.y) || data[o + 2] !== Math.fround(v.z) || data[o + 3] !== Math.fround(v.w)) { v.toArray(data, o); moved = true; } });
    if (moved) touchTexels(skeleton);
  };
  /** Carries the chain in the figure's bone texture (figure-texels.ts), placed afresh each frame as three updates the skeleton. Call before `apply`. */
  const attach = (target: THREE.Skeleton) => {
    skeleton = target; base = reserveTexels(target, chain.length);
    afterSkeletonUpdate(target, update);
  };
  /** Add the push to a material. It reads the chain from whichever figure it draws, so figures with the same skeleton can share it. */
  const apply = (material: THREE.Material) => {
    if (base < 0) throw new Error('Attach the leg chain to its skeleton before applying it to a material');
    const compile = material.onBeforeCompile, key = material.customProgramCacheKey.bind(material), first = base;
    material.customProgramCacheKey = () => `${key()}:cloth-leg-push-${count}@${first}`;
    material.onBeforeCompile = (shader, renderer) => {
      compile.call(material, shader, renderer);
      // Shadow shaders normally omit normals unless they have a displacement map.
      // Leg contact still needs the skinned outward direction in those passes.
      if (material instanceof THREE.MeshDepthMaterial || material instanceof THREE.MeshDistanceMaterial) {
        shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', `
          #ifndef USE_DISPLACEMENTMAP
            #include <beginnormal_vertex>
            #include <morphnormal_vertex>
            #include <skinnormal_vertex>
          #endif
          #include <begin_vertex>`);
      }
      shader.vertexShader = `attribute float ${CLOTH_GAP};
        // Carry p along d, square to the capsule a-b (a cylinder along its length, a sphere beyond either end), out of the
        // capsule and its shadow, onto its far side or the surface falling back from its silhouette; see pushFromLegs.
        vec3 pushFromLeg(vec3 p, vec3 d, vec4 a, vec4 b, float gap) {
          vec3 ab = b.xyz - a.xyz;
          float ab2 = max(dot(ab, ab), 1e-6), u = clamp(dot(p - a.xyz, ab) / ab2, 0., 1.);
          vec3 q = p - (a.xyz + ab * u), e = vec3(d.x, 0., d.z), up = vec3(0., 1., 0.);
          float reach = max(0., mix(a.w, b.w, u) + gap);
          if (reach <= 0.) return p;
          e = dot(e, e) > 1e-6 ? normalize(e) : d;
          e -= ab * (dot(e, ab) / ab2); up -= ab * (dot(up, ab) / ab2);
          e += up * dot(e, up);
          float el = length(e);
          if (el < .05) return p;
          e /= el;
          float B = dot(q, e), x = sqrt(max(0., dot(q, q) - B * B)) / reach - 1.;
          if (x >= 1.) return p;
          float to = x < 0. ? reach * sqrt(-x * (x + 2.)) : -reach * x / (1. - x);
          return to > B ? p + e * (to - B) : p;
        }\n` + withFigureTexels(shader.vertexShader).replace('#include <skinning_vertex>', `#include <skinning_vertex>
        // The chain lives in the bone texture: geometry baked rigid (a corpse at rest) keeps the pose it was baked in.
        #ifdef USE_SKINNING
        if (${CLOTH_GAP} > ${(CLOTH_BIAS / 2).toFixed(1)}) {
          vec3 clothOut = normalize(objectNormal);
          for (int leg = 0; leg < 2; leg++) for (int i = 0; i < ${count - 1}; i++) transformed = pushFromLeg(transformed, clothOut, figureTexel(${first} + leg * ${count} + i), figureTexel(${first} + leg * ${count} + i + 1), ${CLOTH_GAP} - ${CLOTH_BIAS.toFixed(1)});
        }
        #endif`);
    };
  };
  return { chain, update, attach, apply };
}

/** How far a point stands outside the nearest capsule of either leg (negative inside). */
export function clearanceFromLegs(point: THREE.Vector3, chain: THREE.Vector4[]) {
  const count = chain.length / 2, ab = new THREE.Vector3(), away = new THREE.Vector3();
  let least = Infinity;
  for (let leg = 0; leg < 2; leg++) for (let i = 0; i < count - 1; i++) {
    const a = chain[leg * count + i], b = chain[leg * count + i + 1];
    ab.set(b.x - a.x, b.y - a.y, b.z - a.z);
    const t = THREE.MathUtils.clamp(((point.x - a.x) * ab.x + (point.y - a.y) * ab.y + (point.z - a.z) * ab.z) / Math.max(ab.lengthSq(), 1e-6), 0, 1);
    away.set(point.x - a.x - ab.x * t, point.y - a.y - ab.y * t, point.z - a.z - ab.z * t);
    least = Math.min(least, away.length() - (a.w + (b.w - a.w) * t));
  }
  return least;
}

/** The shader's push on the CPU: carries `point` out of every capsule of both legs and its shadow along `out` (unit), `gap` beyond each radius. */
const pushAB = new THREE.Vector3(), pushQ = new THREE.Vector3(), pushE = new THREE.Vector3(), pushUp = new THREE.Vector3();
export function pushFromLegs(point: THREE.Vector3, out: THREE.Vector3, chain: THREE.Vector4[], gap: number) {
  const count = chain.length / 2, ab = pushAB, q = pushQ, e = pushE, up = pushUp;
  for (let leg = 0; leg < 2; leg++) for (let i = 0; i < count - 1; i++) {
    const a = chain[leg * count + i], b = chain[leg * count + i + 1];
    ab.set(b.x - a.x, b.y - a.y, b.z - a.z);
    const ab2 = Math.max(ab.lengthSq(), 1e-6), u = THREE.MathUtils.clamp(((point.x - a.x) * ab.x + (point.y - a.y) * ab.y + (point.z - a.z) * ab.z) / ab2, 0, 1);
    q.set(point.x - a.x - ab.x * u, point.y - a.y - ab.y * u, point.z - a.z - ab.z * u);
    // A negative resting gap can shrink a capsule to nothing; squaring a negative radius would grow it again.
    const reach = Math.max(0, a.w + (b.w - a.w) * u + gap);
    if (reach <= 0) continue;
    // A hem's vertical shading normal must not fling adjacent cloth rows in opposite
    // directions. Carry around the leg horizontally, keeping the drawn normals intact.
    e.set(out.x, 0, out.z);
    if (e.lengthSq() > 1e-6) e.normalize(); else e.copy(out);
    // Square to the leg everywhere, so the line never switches along it; as the leg tilts to level its rise or fall across it grows up to twofold.
    up.set(0, 1, 0);
    e.addScaledVector(ab, -e.dot(ab) / ab2); up.addScaledVector(ab, -up.dot(ab) / ab2);
    e.addScaledVector(up, e.dot(up));
    const el = e.length();
    if (el < .05) continue;
    e.divideScalar(el);
    // The line's distance from the leg, in radii beyond its surface: inside the shadow (x < 0) the target is the far side;
    // beside it the target falls back from the silhouette (0 there) towards the body, out of reach by twice the radius.
    const B = q.dot(e), x = Math.sqrt(Math.max(0, q.lengthSq() - B * B)) / reach - 1;
    if (x >= 1) continue;
    const to = x < 0 ? reach * Math.sqrt(-x * (x + 2)) : -reach * x / (1 - x);
    if (to > B) point.addScaledVector(e, to - B);
  }
  return point;
}

/**
 * The gap a vertex may keep at rest: `gap`, or less where the modelled cloth already overlaps a leg (a loincloth hugging a thigh),
 * so the standing figure is never moved and the cloth shifts only when a leg moves further into it.
 */
export function restingGap(point: THREE.Vector3, out: THREE.Vector3, chain: THREE.Vector4[], gap: number) {
  const moved = (g: number) => pushFromLegs(point.clone(), out, chain, g).distanceToSquared(point) > 1e-8;
  if (!moved(gap)) return gap;
  let low = -Math.max(...chain.map(sample => sample.w)), high = gap;
  for (let step = 0; step < 14; step++) { const middle = (low + high) / 2; if (moved(middle)) high = middle; else low = middle; }
  // A little less, so storing the gap as a float cannot tip a vertex at a leg's edge into a visible move.
  return low - 1e-5;
}

/**
 * Moves a point being built as hanging cloth level out of every capsule of the rest-pose legs, to `clearance` beyond each leg's
 * surface (the capsule less `LEG_PAD`).
 */
export function clearOfLegs(point: THREE.Vector3, chain: THREE.Vector4[], clearance: number) {
  const count = chain.length / 2, ab = new THREE.Vector3(), away = new THREE.Vector3();
  for (let leg = 0; leg < 2; leg++) for (let i = 0; i < count - 1; i++) {
    const a = chain[leg * count + i], b = chain[leg * count + i + 1];
    ab.set(b.x - a.x, b.y - a.y, b.z - a.z);
    const t = THREE.MathUtils.clamp(((point.x - a.x) * ab.x + (point.y - a.y) * ab.y + (point.z - a.z) * ab.z) / Math.max(ab.lengthSq(), 1e-6), 0, 1);
    const reach = a.w + (b.w - a.w) * t - LEG_PAD + clearance;
    away.set(point.x - a.x - ab.x * t, 0, point.z - a.z - ab.z * t);
    const distance = away.length();
    if (distance >= reach) continue;
    if (distance < 1e-6) away.set(0, 0, 1); else away.divideScalar(distance);
    point.addScaledVector(away, reach - distance);
  }
  return point;
}
